import React, { useEffect, useState } from "react";
import { Button } from "src/app/components/Button/Button";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ReactComponent as Stars } from "src/images/ai-stars-white.svg";
import { ReactComponent as AiChat } from "src/images/ai-chat.svg";
import { ReactComponent as TickLightGreyIcon } from "src/images/tick-light-grey.svg";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import DismissibleTooltip from "src/app/components/DismissibleTooltip/DismissibleTooltip";
import Loader from "src/app/components/Loader/Loader";
import { wsAxiosPost } from "src/helpers/wsAxios";
import { API_URLS } from "src/utils/API_URLS";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import { getGlobalTaskMetaFieldList } from "src/redux";
import { TaskType } from "src/types";
import { generateSelectedInfluencersPayload } from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/components/SelectedCreatorsBar/utils";
import { showToast } from "src/app/methods/showToast";
import { ProjectBasicData } from "src/redux/project/types";
import { GlobalFieldBase } from "../../../../../redux/settings/settingsReducer";
import { openAIChatWithContext } from "src/redux/ai-chat/aiChatActions";

interface StartConversationButtonProps {
  wsWorkspaceUuid: string;
  onClearAllSelected: () => void;
  wsProjectUuid: string;
  selectedInfluencersOnResultsUrls: any;
  searchAudienceResult: any;
  projectBasicData: ProjectBasicData;
}

export default function CreatorSearchStartWithAiConversation({
  wsWorkspaceUuid,
  onClearAllSelected,
  wsProjectUuid,
  selectedInfluencersOnResultsUrls,
  searchAudienceResult,
  projectBasicData,
}: StartConversationButtonProps) {
  const [
    createAndCalculateAiPoweredFieldLoading,
    setCreateAndCalculateAiPoweredFieldLoading,
  ] = useState(false);
  const {
    projectReducer: { globalMetaFieldsList, isGlobalMetaFieldsListLoading },
  } = useSelector((state: RootState) => state);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      getGlobalTaskMetaFieldList(
        wsWorkspaceUuid,
        wsProjectUuid,
        TaskType.Creator,
      ),
    );
  }, [dispatch, wsProjectUuid, wsWorkspaceUuid]);

  const tasksData = generateSelectedInfluencersPayload(
    selectedInfluencersOnResultsUrls,
    searchAudienceResult,
  );

  const createAndCalculateAiPoweredField = (
    wsGlobalTaskMetaFieldUuid: string,
    wsProjectUuid: string,
    fieldName: string,
  ) => {
    setCreateAndCalculateAiPoweredFieldLoading(true);

    wsAxiosPost(
      API_URLS.createGlobalTaskMetaFieldAndGenerateValue,
      {
        wsGlobalTaskMetaFieldUuid,
        wsProjectUuid,
        tasksData,
        wsWorkspaceUuid,
        wsTaskType: TaskType.Creator,
      },
      undefined,
      () => {},
      () => {
        showToast(
          "info",
          <IDHFormattedMessage id="ws_info" defaultMessage="Info" />,
          <>
            <IDHFormattedMessage
              id={"toast_the_ai_field_is_being_calculated"}
              defaultMessage={
                "The AI field is being calculated. The result will appear in the <b>Creators tab</b> - column"
              }
            />
            <b> {fieldName}. </b>
            <IDHFormattedMessage
              id={"toast_the_ai_field_this_may_take_a_moment"}
              defaultMessage={"This may take a moment"}
            />
          </>,
        );
      },
      () => {
        setTimeout(() => {
          // Intentional delay to prevent rapid repeated clicks
          setCreateAndCalculateAiPoweredFieldLoading(false);
        }, 1000);
      },
    );
  };

  const CreatorAiPoweredMetaFields = globalMetaFieldsList.filter(
    (field) => field?.data?.aiMetaValueService != null,
  );

  const ASK_AI_TOOLTIP_KEY = "askAITooltipHiddenSingleProject" as const;
  const renderAiFieldItem = (
    field: GlobalFieldBase,
    options?: {
      disabled?: boolean;
      onClick?: () => void;
    },
  ) => (
    <DropdownMenuItem
      key={field.uuid}
      className="ws-button--white dropdown__menu-item--multiline"
      data-qa={`ask-ai-field--${field.origin}`}
      onClick={options?.onClick}
      disabled={options?.disabled}
    >
      <Stars />
      <div>
        <div className="title">
          {field.name} {field.existsInProject && <TickLightGreyIcon />}
        </div>
        <span className="description">{field.description}</span>
      </div>
    </DropdownMenuItem>
  );

  return (
    <>
      <DismissibleTooltip
        storageKey={ASK_AI_TOOLTIP_KEY}
        tooltipId="ask-ai-tooltip"
        title={
          <IDHFormattedMessage id="ws_ask_ai_title" defaultMessage="Ask AI" />
        }
        description={
          <IDHFormattedMessage
            id="ws_ask_ai_description"
            defaultMessage="Upload the campaign brief and take advantage of AI support."
          />
        }
        checkboxLabel={
          <IDHFormattedMessage
            id="ws_dont_show_me_again"
            defaultMessage="Don't show me again"
          />
        }
        confirmLabel={<IDHFormattedMessage id="ws_ok" defaultMessage="Ok" />}
      />

      <DismissibleTooltip
        storageKey="ask-ai-research-tooltip"
        tooltipId="ask-ai-research-tooltip"
        title={
          <IDHFormattedMessage
            id="ws_ask_ai_research_title"
            defaultMessage="Ask AI unavailable"
          />
        }
        description={
          <IDHFormattedMessage
            id="ws_ask_ai_research_description"
            defaultMessage="AI fields can be created only for active campaigns."
          />
        }
        checkboxLabel={
          <IDHFormattedMessage
            id="ws_dont_show_me_again"
            defaultMessage="Don't show me again"
          />
        }
        confirmLabel={<IDHFormattedMessage id="ws_ok" defaultMessage="Ok" />}
      />

      <Dropdown toBottom>
        {(isOpen, setIsOpen) => (
          <>
            <Button
              size="large"
              variant="white"
              data-tooltip-id="ask-ai-tooltip"
              data-qa="ask-ai-button"
              onClick={() => setIsOpen((prev) => !prev)}
            >
              <Stars />
              <IDHFormattedMessage id="ws_ask_ai" defaultMessage="Ask AI..." />
            </Button>

            <DropdownMenu isOpen={isOpen}>
              {isGlobalMetaFieldsListLoading && (
                <DropdownMenuItem
                  className="ws-button ws-button--loader"
                  data-qa="create-ai-fields-button"
                  key="create-ai-fields-button"
                  onClick={() => null}
                >
                  <Loader />
                </DropdownMenuItem>
              )}

              {!isGlobalMetaFieldsListLoading && (
                <>
                  <div style={{ maxHeight: 207, overflow: "auto" }}>
                    {projectBasicData?.status === "active" &&
                      CreatorAiPoweredMetaFields.map((field) =>
                        renderAiFieldItem(field, {
                          onClick: () =>
                            createAndCalculateAiPoweredField(
                              field.uuid,
                              wsProjectUuid,
                              field.name,
                            ),
                          disabled: createAndCalculateAiPoweredFieldLoading,
                        }),
                      )}

                    {projectBasicData?.status === "research" && (
                      <div data-tooltip-id="ask-ai-research-tooltip">
                        {CreatorAiPoweredMetaFields.map((field) =>
                          renderAiFieldItem(field, {
                            disabled: true,
                          }),
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              <DropdownMenuItem
                className="ws-button--white ai-start-conversation"
                data-qa="talk-with-ai-button"
                key="talk-with-ai-button"
                onClick={() => {
                  dispatch(
                    openAIChatWithContext({
                      type: "socialUsername",
                      data: tasksData.map((creator: { name: string }) => ({
                        title: creator.name,
                      })),
                    }),
                  );
                  setIsOpen(false);
                  onClearAllSelected();
                }}
              >
                <AiChat />
                <IDHFormattedMessage
                  id="ws_start_conversation_ai"
                  defaultMessage="Start conversation"
                />
              </DropdownMenuItem>
            </DropdownMenu>
          </>
        )}
      </Dropdown>
    </>
  );
}
